import React, { useEffect } from "react";
import { Combobox } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/solid";
import { useTranslation } from "react-i18next";

export default function AutoComplete({
  label,
  field_name,
  error,
  required,
  onChange,
  values,
  borderColor,
  value,
  activeElement,
  setActiveElement,
  onBlur,
}) {
  const [selected, setSelected] = React.useState(null);
  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [closed, setClosed] = React.useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const matchedValue = values.find((val) => val.value === value) || null;
    setSelected(matchedValue);
    setQuery(matchedValue ? matchedValue.label : '');
    setOpen(false);
  }, [value, values]);

  const filteredValues = () => {
    const filtered = query === ''
      ? values
      : values.filter((val) =>
        val.label
          .toLowerCase()
          .replace(/\s+/g, '')
          .includes(query.toLowerCase().replace(/\s+/g, ''))
      );

    return filtered;
  };

  const select = (selectedValue) => {
    setSelected(selectedValue);
    onChange(field_name, selectedValue?.value);
    setActiveElement(field_name);

    setClosed(true);
    setTimeout(() => {
      setOpen(false);
      setClosed(false);
    }, 100);
  };

  const buttonClick = () => {
    setQuery('');
    setActiveElement(field_name);
    setOpen(activeElement !== field_name ? true : !open);
  };

  return (
    <div className="w-full relative">
      <Combobox value={selected} onChange={select} by="value">
        <Combobox.Input
          className="
            peer border flex justify-between items-center h-12 w-full disabled:opacity-60
            px-4 py-2 bg-white text-md
            font-medium rounded-lg outline-none pl-6
          "
          displayValue={(item) => (typeof item === 'string' ? item : item?.label) || ""}
          onChange={(event) => {
            const newValue = event.target.value;
            setOpen(true);
            setQuery(newValue);

            const match = values.find(val => val.label.toLowerCase() === newValue.toLowerCase());
            if (match) {
              select(match);
            } else {
              setSelected(null);
              onChange(field_name, null);
            }
          }}
          onFocus={() => {
            if (!closed) {
              setOpen(true);
              setActiveElement(field_name);
            }
          }}
          placeholder={required ? `${label}*` : label}
          style={{ borderColor: error ? "#dc2626" : borderColor }}
          onBlur={() => {
            onBlur(field_name, value);
          }}
        />

        <Combobox.Button
          className="absolute inset-y-0 right-2 flex items-center pr-2"
          onClick={buttonClick}
        >
          <ChevronDownIcon
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </Combobox.Button>
        {open && activeElement === field_name && (
          <Combobox.Options
            static
            className="absolute mt-1 z-50 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
          >
            {filteredValues().length === 0 && query !== '' ? (
              <div className="relative cursor-default select-none py-2 px-4 text-gray-500 italic">
                {t("lead.noProcedure")}
              </div>
            ) : (
              filteredValues().map((val) => (
                <Combobox.Option
                  key={val.value}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-6 pr-4 ${active ? "bg-neutral-100 text-black" : "bg-white text-black"
                    }`
                  }
                  value={val}
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={`block ${selected ? "font-medium" : "font-normal"
                          }`}
                      >
                        {val.label}
                      </span>
                    </>
                  )}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        )}
      </Combobox>

      {value && (
        <label
          className="
            absolute rounded-lg bg-white text-xs left-0 -top-2 ml-4 pt-1 px-2
            peer-placeholder-shown:text-md peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-3
            peer-focus:text-xs text-gray-400 peer-focus:peer-valid::text-prm-base peer-focus:text-prm-base
            peer-focus:-top-2 transition-all label-invalid
          "
        >
          {label}
          {required && <span>*</span>}
        </label>
      )}

      {error && (
        <div className="text-red-600 ml-4 text-xs px-2 -bottom-1 bg-white rounded absolute">
          {error}
        </div>
      )}
    </div>
  );
}
